import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TextInput } from '../../Shared/Component/forms/TextInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { getEmailValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { forgotPassword } from '../Services/UserService';

function ForgotPasswordComponent() {
  const {
    values,
    handleInputChange,
  } = useFormController<{ email: string }>({
    email: ""
  });

  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [loading, setLoading] = useState(false);
  const { policy } = useValidationPolicy();

  const handleSubmit = async () => {
    const emailError = getEmailValidationError(values.email, policy);
    if (emailError) {
      setAlert({
        variant: "error",
        title: "Invalid Email",
        message: emailError,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    setLoading(true);
    setAlert(null);

    const response = await forgotPassword({ email: values.email.trim() });

    if (response.success) {
      setAlert({
        variant: "success",
        title: "Check Your Email",
        message: response.data?.message || response.message,
        autoCloseMs: 8000,
        onClose: () => setAlert(null),
      });
    } else {
      setAlert({
        variant: "error",
        title: "Request Error",
        message: response.message,
        autoCloseMs: 6000,
        onClose: () => setAlert(null),
      });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Alert Section */}
      <div className="mb-4">
        {alert && (
          <Alert
            variant={alert.variant}
            title={alert.title}
            message={alert.message}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
      {/* Decorative colorful background glows for that vibrant cyberpunk feel */}
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        <h2 className="mt-6 text-center text-3xl font-extrabold bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
          Forgot your password?
        </h2>
        <p className="mt-2 text-center text-sm text-slate-400 font-medium tracking-wide">
          Enter the email associated with your account and we'll send you a
          link to reset your password.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-slate-900/80 backdrop-blur-xl py-8 px-4 shadow-2xl shadow-cyan-950/20 border border-slate-800 rounded-xl sm:px-10 relative before:absolute before:inset-0 before:p-[1px] before:bg-gradient-to-b before:from-slate-700 before:to-transparent before:rounded-xl before:-z-10">

          <div className="space-y-6">
            <div className="group">
              <TextInput
                label="Email Address"
                name="email"
                type="email"
                placeholder="name@company.com"
                value={values.email}
                onChange={handleInputChange("email")}
                className="text-slate-200 focus:border-cyan-400 focus:ring-cyan-400/20"
              />
            </div>

            <div>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-lg shadow-cyan-500/20 text-sm font-semibold text-slate-950 bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400 hover:from-cyan-300 hover:via-indigo-300 hover:to-fuchsia-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-cyan-400 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Sending reset link..." : "Send Reset Link"}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-cyan-400 hover:text-cyan-300 font-medium tracking-wide"
              >
                Back to Login
              </Link>
            </div>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-slate-900 text-cyan-400/80 font-mono tracking-wider text-xs uppercase">
                  Secure Encrypted Reset
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordComponent;
